import React, { useState, useEffect, useContext, useCallback, useMemo, memo } from 'react';
import Header from './Header';
import "./Orders.css";
import OrdersContext from './OrdersContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBoxOpen, faStore, faStar as faStarSolid } from '@fortawesome/free-solid-svg-icons';
import { faStar as faStarOutline } from '@fortawesome/free-regular-svg-icons';
import { useLocation } from 'react-router-dom';
import "./Ratings.css"; 

const LoadingSpinner = memo(() => (
    <div className="flipkart-spinner">
        <div className="spinner-container">
            <div className="spinner"></div>
            <div className="loading-text">Loading your orders...</div>
        </div>
    </div>
));

// ── Star row (interactive) ───────────────────────────────────────────────────
const StarPicker = memo(({ value, onChange }) => {
    const [hovered, setHovered] = useState(0);
    const LABELS = ['', 'Poor', 'Fair', 'Good', 'Very Good', 'Excellent'];
    const active = hovered || value;
    return (
        <div>
            <div className="star-picker" onMouseLeave={() => setHovered(0)}>
                {[1, 2, 3, 4, 5].map(star => (
                    <button
                        key={star}
                        type="button"
                        className={`star-btn${active >= star ? ' filled' : ''}`}
                        onMouseEnter={() => setHovered(star)}
                        onClick={() => onChange(star)}
                        aria-label={`${star} star`}
                    >
                        <FontAwesomeIcon icon={active >= star ? faStarSolid : faStarOutline} />
                    </button>
                ))}
            </div>
            {active > 0 && <div className="star-label">{LABELS[active]}</div>}
        </div>
    );
});

// ── Star row (read-only display) ─────────────────────────────────────────────
const StarDisplay = memo(({ rating }) => (
    <span className="star-display" aria-label={`${rating} out of 5`}>
        {[1, 2, 3, 4, 5].map(s => (
            <FontAwesomeIcon
                key={s}
                icon={faStarSolid}
                className={s <= Math.round(rating) ? 'star-filled' : 'star-empty'}
            />
        ))}
        <span className="star-display-value">{Number(rating).toFixed(1)}</span>
    </span>
));

// ── Rating Modal ─────────────────────────────────────────────────────────────
const RatingModal = memo(({ item, onClose, onSubmitted, apiUrl }) => {
    const [rating,     setRating]     = useState(0);
    const [feedback,   setFeedback]   = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error,      setError]      = useState('');

    const handleSubmit = async (mergedFeedback) => {
        if (!rating) { setError('Please select a star rating before submitting.'); return; }
        setSubmitting(true);
        setError('');
        const finalFeedback = mergedFeedback !== undefined ? mergedFeedback : feedback.trim();
        try {
            const userId = localStorage.getItem('userid') || localStorage.getItem('user_id');
            const token  = localStorage.getItem('token');
            const res = await fetch(`${apiUrl}/api/items/${item.item_id}/review`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({
                    rating,
                    feedback: finalFeedback,
                    user_id: Number(userId),
                }),
            });
            if (!res.ok) {
                const err = await res.json();
                throw new Error(err.error || res.statusText);
            }
            const data = await res.json();
            onSubmitted(item.item_id, rating, finalFeedback, data.avg_rating, data.rating_count);
            onClose();
        } catch (e) {
            setError(e.message || 'Failed to submit review. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    const CHIPS = ['Good quality', 'Fast delivery', 'As described', 'Great value', 'Recommended'];
    const [selectedChips, setSelectedChips] = useState([]);

    const toggleChip = (chip) => {
        setSelectedChips(prev =>
            prev.includes(chip) ? prev.filter(c => c !== chip) : [...prev, chip]
        );
    };

    // Merge chips into feedback on submit
    const buildFeedback = () => {
        const chipText = selectedChips.join(', ');
        if (chipText && feedback.trim()) return `${chipText}. ${feedback.trim()}`;
        return chipText || feedback.trim();
    };

    return (
        <div className="rating-modal-overlay" onClick={onClose}>
            <div className="rating-modal" onClick={e => e.stopPropagation()}>

                {/* Orange header band */}
                <div className="rating-modal-header">
                    <button className="rating-modal-close" onClick={onClose} aria-label="Close">×</button>
                    {item.image_url && (
                        <img src={item.image_url} alt={item.product_name} className="rating-modal-img" />
                    )}
                    <span className="rating-modal-name">{item.product_name}</span>
                </div>

                {/* White body */}
                <div className="rating-modal-body">
                    <h3 className="rating-modal-title">Rate this product</h3>
                    <p className="rating-modal-subtitle">Your honest feedback helps other shoppers</p>

                    <StarPicker value={rating} onChange={setRating} />

                    {/* Quick chips — appear after star is picked */}
                    {rating > 0 && (
                        <div className="feedback-tags">
                            {CHIPS.map(chip => (
                                <button
                                    key={chip}
                                    type="button"
                                    className={`feedback-tag${selectedChips.includes(chip) ? ' selected' : ''}`}
                                    onClick={() => toggleChip(chip)}
                                >
                                    {chip}
                                </button>
                            ))}
                        </div>
                    )}

                    <div className="rating-divider">Write a review</div>

                    <textarea
                        className="feedback-textarea"
                        placeholder="Share details of your experience…"
                        value={feedback}
                        onChange={e => setFeedback(e.target.value)}
                        maxLength={500}
                        rows={3}
                    />
                    <div className="feedback-charcount">{feedback.length}/500</div>

                    {error && <div className="rating-error">⚠ {error}</div>}

                    <div className="rating-modal-actions">
                        <button
                            className="rating-submit-btn"
                            onClick={() => handleSubmit(buildFeedback())}
                            disabled={submitting}
                        >
                            {submitting ? 'Submitting…' : 'Submit Review'}
                        </button>
                        <button className="rating-cancel-btn" onClick={onClose} disabled={submitting}>
                            Cancel
                        </button>
                    </div>
                </div>

            </div>
        </div>
    );
});

// ────────────────────────────────────────────────────────────────────────────

function Orders() {
    const API_URL = process.env.REACT_APP_API_URL || 'https://pyate-backend-production-9749.up.railway.app';

    const {
        orders,
        customerOrders,
        ordersLoading,
        fetchOrders,
        fetchOrdersCount,
        setOrders,
        setCustomerOrders,
    } = useContext(OrdersContext);

    const hasFetched = orders !== null && customerOrders !== null;
    const loading    = ordersLoading || !hasFetched;

    const [merchantStatuses,   setMerchantStatuses]   = useState({});
    const [orderPendingCancel, setOrderPendingCancel] = useState(null);
    const [activeTab,          setActiveTab]          = useState('your-orders');
    const [filter,             setFilter]             = useState('all');
    // { item_id, product_name, image_url } — which item is in the review modal
    const [reviewTarget,       setReviewTarget]       = useState(null);
    // map of item_id → { rating, avg_rating, rating_count } for optimistic UI
    const [reviewedItems,      setReviewedItems]      = useState({});

    const location = useLocation();

    useEffect(() => { fetchOrders(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (location.state?.selectedTab === 'product-orders') setActiveTab('product-orders');
        if (location.state?.selectedTab === 'your-orders')    setActiveTab('your-orders');
    }, [location.state]);

    useEffect(() => {
        if (!customerOrders?.length) return;
        const initial = {};
        customerOrders.forEach(o => { if (o.status) initial[o.order_id] = o.status; });
        setMerchantStatuses(initial);
    }, [customerOrders]);

    // ── Filters ──────────────────────────────────────────────────────────────
    const applyDateFilter = useCallback((list) => {
        if (filter === 'all') return list;
        const now = new Date();
        return list.filter(o => {
            const days = (now - new Date(o.order_date)) / 86400000;
            switch (filter) {
                case 'last30': return days <= 30;
                case 'past6':  return days <= 180;
                case '2023':   return new Date(o.order_date).getFullYear() === now.getFullYear();
                case 'older':  return days > 180;
                default:       return true;
            }
        });
    }, [filter]);

    const filteredOrders        = useMemo(() => applyDateFilter(orders        || []), [orders,        applyDateFilter]);
    const filteredCustomerOrders = useMemo(() => applyDateFilter(customerOrders || []), [customerOrders, applyDateFilter]);

    const yourActiveCount = useMemo(() =>
        (orders || []).filter(o => {
            const s = (o.status || '').toLowerCase();
            return s === 'processing' || s === 'out for delivery';
        }).length,
    [orders]);

    // ── Handlers ─────────────────────────────────────────────────────────────
    const handleStatusUpdate = useCallback(async (orderId, newStatus) => {
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_URL}/api/update_order_status?order_id=${orderId}`, {
                method: 'PUT',
                headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ status: newStatus }),
            });
            if (!res.ok) {
                const err = await res.json();
                throw new Error(err.details || res.statusText);
            }
            setCustomerOrders(prev =>
                prev.map(o => o.order_id === orderId ? { ...o, status: newStatus } : o)
            );
            setMerchantStatuses(prev => ({ ...prev, [orderId]: newStatus }));
            alert('Order status updated successfully');
        } catch (e) {
            console.error('Error updating order status:', e);
            alert('Failed to update order status. Please try again.');
        }
    }, [API_URL, setCustomerOrders]);

    const handleCancelOrder = useCallback(async (orderId) => {
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_URL}/api/delete_order?order_id=${orderId}`, {
                method: 'DELETE',
                headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
            });
            if (!res.ok) throw new Error(res.statusText);
            setOrders(prev => prev.filter(o => o.order_id !== orderId));
            setOrderPendingCancel(null);
            fetchOrdersCount();
            alert('Order cancelled successfully');
        } catch (e) {
            console.error('Error cancelling order:', e);
            alert('Failed to cancel order. Please try again.');
        }
    }, [API_URL, setOrders, fetchOrdersCount]);

    const canCancelOrder = useCallback((status, orderDate) => {
        if (!status) return false;
        const s   = status.toLowerCase();
        const hrs = (new Date() - new Date(orderDate)) / 3600000;
        return s === 'processing' || (s === 'shipped' && hrs < 24);
    }, []);

    // Called when user successfully submits a review — update UI optimistically
    const handleReviewSubmitted = useCallback((itemId, rating, feedback, avgRating, ratingCount) => {
        setReviewedItems(prev => ({
            ...prev,
            [itemId]: { rating, feedback, avg_rating: avgRating, rating_count: ratingCount },
        }));
    }, []);

    // ── Render helpers ────────────────────────────────────────────────────────
    const renderRateButton = (item, orderStatus) => {
        if ((orderStatus || '').toLowerCase() !== 'delivered') return null;
        const reviewed = reviewedItems[item.item_id];
        return (
            <button
                className={`leave-feedback-btn${reviewed ? ' reviewed' : ''}`}
                onClick={() => setReviewTarget(item)}
            >
                <FontAwesomeIcon icon={faStarSolid} />
                {reviewed ? 'Update Review' : 'Rate & Review'}
            </button>
        );
    };

    const renderReviewBadge = (item) => {
        const reviewed = reviewedItems[item.item_id];
        if (!reviewed) return null;
        return (
            <div className="review-submitted-badge">
                <StarDisplay rating={reviewed.rating} />
                {reviewed.feedback && (
                    <span className="review-feedback-preview">"{reviewed.feedback}"</span>
                )}
            </div>
        );
    };

    // ── Tab content ───────────────────────────────────────────────────────────
    const renderOrdersContent = () => {
        if (activeTab === 'your-orders') {
            return (
                <div className="orders-section">
                    <div className="orders-filter">
                        <span>Filter:</span>
                        <select value={filter} onChange={e => setFilter(e.target.value)}>
                            <option value="all">All Orders</option>
                            <option value="last30">Last 30 days</option>
                            <option value="past6">Past 6 months</option>
                            <option value="2023">This Year</option>
                            <option value="older">Older Orders</option>
                        </select>
                    </div>

                    {filteredOrders.length === 0 && hasFetched ? (
                        <div className="empty-orders">
                            <FontAwesomeIcon icon={faBoxOpen} className="empty-orders-icon" />
                            <h3>No orders found</h3>
                            <p>{filter !== 'all' ? 'No orders match your filter criteria' : "You haven't placed any orders yet"}</p>
                            <button className="continue-shopping-btn">
                                <a href='/shop' style={{ textDecoration: 'none', color: 'white' }}>Continue Shopping</a>
                            </button>
                        </div>
                    ) : (
                        <div className="orders-list">
                            {filteredOrders.map(order => (
                                <div className="order-card" key={order.order_id}>
                                    <div className="order-header">
                                        <div className="order-meta">
                                            <div className="order-date">
                                                <span>ORDER PLACED</span>
                                                <span>{new Date(order.order_date).toLocaleDateString('en-IN', {
                                                    day: 'numeric', month: 'short', year: 'numeric'
                                                })}</span>
                                            </div>
                                            <div className="order-total">
                                                <span>TOTAL</span>
                                                <span>₹{order.total_amount.toFixed(2)}</span>
                                            </div>
                                        </div>
                                        <div className="order-id">
                                            <span>ORDER #{order.order_id}</span>
                                            <a href="#">Invoice</a>
                                        </div>
                                    </div>

                                    <div className="order-status-container">
                                        <div className={`order-status status-${order.status || 'processing'}`}>
                                            {order.status
                                                ? order.status.charAt(0).toUpperCase() + order.status.slice(1)
                                                : 'Processing'}
                                        </div>
                                        {canCancelOrder(order.status, order.order_date) && (
                                            <button
                                                className="cancel-order-btn"
                                                onClick={() => setOrderPendingCancel(order.order_id)}
                                            >
                                                Cancel Order
                                            </button>
                                        )}
                                    </div>

                                    <div className="order-items">
                                        {order.items && order.items.map(item => (
                                            <div className="order-item" key={item.order_item_id}>
                                                <img
                                                    src={item.image_url || 'https://via.placeholder.com/70'}
                                                    alt={item.product_name}
                                                    className="order-item-image"
                                                    loading="lazy"
                                                />
                                                <div className="order-item-details">
                                                    <div className="order-item-name">{item.product_name}</div>
                                                    <div className="order-item-price">₹{item.price.toFixed(2)}</div>
                                                    <div className="order-item-quantity">Quantity: {item.quantity}</div>
                                                    {renderReviewBadge(item)}
                                                </div>
                                                <div className="order-item-actions">
                                                    {renderRateButton(item, order.status)}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            );
        } else if (activeTab === 'product-orders') {
            return (
                <div className="orders-section">
                    {filteredCustomerOrders.length === 0 && hasFetched ? (
                        <div className="empty-orders">
                            <FontAwesomeIcon icon={faStore} className="empty-orders-icon" />
                            <h3>No orders found</h3>
                            <p>{filter !== 'all' ? 'No orders match your filter criteria' : "Customers haven't placed any orders with your products yet"}</p>
                        </div>
                    ) : (
                        <div className="orders-list">
                            {filteredCustomerOrders.map(order => (
                                <div className="order-card" key={order.order_id}>
                                    <div className="order-header">
                                        <div className="order-meta">
                                            <div className="order-date">
                                                <span>ORDER PLACED</span>
                                                <span>{new Date(order.order_date).toLocaleDateString('en-IN', {
                                                    day: 'numeric', month: 'short', year: 'numeric'
                                                })}</span>
                                            </div>
                                            <div className="order-total">
                                                <span>TOTAL</span>
                                                <span>₹{order.total_amount.toFixed(2)}</span>
                                            </div>
                                        </div>
                                        <div className="order-id">
                                            <span>ORDER #{order.order_id}</span>
                                            <a href="#">Invoice</a>
                                        </div>
                                    </div>

                                    <div className="order-status-container">
                                        <div className={`order-status status-${merchantStatuses[order.order_id] || order.status || 'processing'}`}>
                                            {(merchantStatuses[order.order_id] || order.status || 'processing')
                                                .charAt(0).toUpperCase() +
                                             (merchantStatuses[order.order_id] || order.status || 'processing').slice(1)}
                                        </div>
                                        <select
                                            value={merchantStatuses[order.order_id] || order.status || 'processing'}
                                            onChange={e => handleStatusUpdate(order.order_id, e.target.value)}
                                            className="status-dropdown"
                                        >
                                            <option value="processing">Processing</option>
                                            <option value="out for delivery">Out for Delivery</option>
                                            <option value="cancelled order">Order delivery cancelled</option>
                                            <option value="delivered">Delivered</option>
                                        </select>
                                    </div>

                                    {/* Product rating summary for merchant view */}
                                    {(order.avg_rating > 0) && (
                                        <div className="merchant-rating-summary">
                                            <StarDisplay rating={order.avg_rating} />
                                            <span className="rating-count-text">
                                                {order.rating_count} {order.rating_count === 1 ? 'review' : 'reviews'}
                                            </span>
                                            {order.latest_feedback && (
                                                <span className="merchant-latest-feedback">
                                                    Latest: "{order.latest_feedback}"
                                                </span>
                                            )}
                                        </div>
                                    )}

                                    <div className="customer-info">
                                        <div className="customer-detail">
                                            <span style={{ fontWeight: 'bold' }}>Customer: </span>
                                            <span>{order.customer_name || 'N/A'}</span>
                                        </div><br />
                                        <div className="customer-detail">
                                            <span style={{ fontWeight: 'bold' }}>Email: </span>
                                            <span>{order.customer_email || 'N/A'}</span>
                                        </div>
                                    </div>

                                    <div className="order-items">
                                        <div className="order-item">
                                            {order.image_url && (
                                                <img
                                                    src={order.image_url}
                                                    alt={order.product_name}
                                                    className="order-item-image"
                                                    loading="lazy"
                                                    style={{ marginRight: '16px' }}
                                                />
                                            )}
                                            <div className="order-item-details">
                                                <h4>Item Details:</h4>
                                                <div className="order-item-name">{order.product_name}</div>
                                                <div className="order-item-price">₹{order.item_price.toFixed(2)}</div>
                                                <div className="order-item-quantity">Quantity: {order.quantity}</div>
                                                <div className="order-item-page">From: {order.page_name}</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            );
        }
        return null;
    };

    return (
        <>
            <Header />
            <div className="my-orders-container">
                <div className="main-nav">
                    <h2 className="nav-header">My Account</h2>
                    <ul>
                        <li
                            className={activeTab === 'your-orders' ? 'active' : ''}
                            onClick={() => setActiveTab('your-orders')}
                            style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                        >
                            <span>Your Orders</span>
                            <span className="orders-count-badge">{yourActiveCount}</span>
                        </li>
                        <li
                            className={activeTab === 'product-orders' ? 'active' : ''}
                            onClick={() => setActiveTab('product-orders')}
                        >
                            <span>Orders for Your Products</span>
                        </li>
                    </ul>
                </div>

                <div className="orders-content">
                    <div className="orders-header">
                        <h1>{activeTab === 'your-orders' ? 'Your Orders' : 'Orders for Your Products'}</h1>
                    </div>
                    {loading ? <LoadingSpinner /> : renderOrdersContent()}
                </div>
            </div>

            {/* Cancel Order Modal */}
            {orderPendingCancel && (
                <div className="popup-overlay">
                    <div className="popup-modal">
                        <p>Are you sure you want to cancel this order?</p>
                        <div style={{ display: 'flex', gap: '10px', marginTop: '15px' }}>
                            <button className="confirm-remove-btn" onClick={() => handleCancelOrder(orderPendingCancel)}>Yes</button>
                            <button className="cancel-remove-btn" onClick={() => setOrderPendingCancel(null)}>No</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Rating Modal */}
            {reviewTarget && (
                <RatingModal
                    item={reviewTarget}
                    onClose={() => setReviewTarget(null)}
                    onSubmitted={handleReviewSubmitted}
                    apiUrl={API_URL}
                />
            )}
        </>
    );
}

export default Orders;